/**
 * Deleting a form: a dialog with the count, not a `window.confirm` (rule 5).
 *
 * Opened from the forms list's row menu and, since R8, from the bottom of
 * Form settings — one dialog, so both say the same thing.
 */
import { useState } from 'react';
import { FormSummary, deleteFormApi } from '../../api/forms';
import { Modal } from '../../components/fs';

/** Delete is a dialog with the count, not a `window.confirm` (rule 5). */
export default function DeleteFormModal({ form, onClose, onDone, onError }: {
  /** the form, with how many responses it holds — the count decides whether the name must be typed */
  form: Pick<FormSummary, 'id' | 'name' | 'submission_count'>;
  onClose: () => void;
  onDone: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');
  // A form with responses is a form somebody's records live in, so deleting it
  // asks for its name typed — the treatment tenant deletion already gets.
  const needsName = form.submission_count > 0;

  async function remove() {
    if (busy) return;
    setBusy(true);
    try { await deleteFormApi(form.id); onDone(); }
    catch (e: any) { onError(e.message); onClose(); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      title={<>Delete <b>{form.name}</b>?</>} narrow onClose={onClose}
      footer={<>
        <button
          className="fs-btn danger" onClick={remove}
          disabled={busy || (needsName && typed.trim() !== form.name)}
        >
          Delete form
        </button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      <div className="fs-consequence warn">
        {needsName ? (
          <><b>{form.submission_count.toLocaleString()} responses will be deleted with it.</b>{' '}
            Uploaded files, delivery history and revisions go too. This cannot be undone.</>
        ) : (
          <>This form has no responses. Deleting it frees a form slot immediately.</>
        )}
      </div>
      {needsName && (
        <div className="fs-field" style={{ marginTop: 14 }}>
          <label htmlFor="del-name">Type <b>{form.name}</b> to confirm</label>
          <input id="del-name" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} />
        </div>
      )}
    </Modal>
  );
}
